import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toExportCanvas } from '../components/exportCanvas';
import { tooltipFlipTransform } from '../components/tooltipPlacement';
import { useChartCanvas, useContainerSize } from '../components/useChartCanvas';
import type { Size } from '../types';
import {
  findRadialPointAt,
  projectRadialPoints,
  toLegendEntries,
  toRadialLayout,
  type ProjectedRadialPoint,
} from './radialLayout';
import { LEGEND_FONT, renderRadial } from './renderRadial';
import {
  toCurrentPointer,
  toKeyboardPointer,
  toNextPointer,
  type PointerState,
} from './radialHover';
import { isRadialNavigationKey, toKeyboardHover } from './radialKeyboard';
import { RadialTooltip } from './RadialTooltip';
import type { RadialMarkType, RadialPlot } from './types';

export type RadialAccessibleNameParts = {
  seriesCount: number;
  pointCount: number;
};

export type RadialChartProps = {
  plot: RadialPlot;
  markType: RadialMarkType;
  format: (value: number) => string;
  /** Names the series folded into the tail, for the legend. */
  formatOtherSeries: (count: number) => string;
  formatAccessibleName: (parts: RadialAccessibleNameParts) => string;
  /** Translated axis titles, drawn top left. */
  axisCaptions: readonly string[];
  onExportCanvasAvailable?: (
    createExportCanvas: (() => OffscreenCanvas | null) | null
  ) => void;
  exportCaptions?: readonly string[];
  onDrawableChange?: (isDrawable: boolean) => void;
  surfaceColor: string;
  inkColor: string;
  /** The hover tooltip's border. The canvas grid is drawn from `inkColor`. */
  tooltipBorderColor: string;
  emptyState?: React.ReactNode;
};

const hasAnyValue = (plot: RadialPlot): boolean =>
  plot.series.some(({ points }) => points.some(({ value }) => value !== null));

const countValues = (plot: RadialPlot): number =>
  plot.series.reduce(
    (count, { points }) =>
      count + points.filter(({ value }) => value !== null).length,
    0
  );

const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

export const RadialChart = ({
  plot,
  markType,
  format,
  formatOtherSeries,
  formatAccessibleName,
  axisCaptions,
  onExportCanvasAvailable,
  exportCaptions,
  onDrawableChange,
  surfaceColor,
  inkColor,
  tooltipBorderColor,
  emptyState,
}: RadialChartProps) => {
  const captionsKey = JSON.stringify(axisCaptions);
  const resolvedCaptions = useMemo<string[]>(
    () => JSON.parse(captionsKey),
    [captionsKey]
  );
  const legendEntries = useMemo(
    () => toLegendEntries(plot, formatOtherSeries),
    [plot, formatOtherSeries]
  );

  const [pointer, setPointer] = useState<PointerState | null>(null);
  const current = useMemo(
    () => toCurrentPointer(pointer, plot),
    [pointer, plot]
  );
  const hovered = current?.hover ?? null;
  const tooltipAt = current?.at ?? null;
  const projected = useRef<ProjectedRadialPoint[]>([]);

  const paint = useCallback(
    (context: CanvasRenderingContext2D, viewport: Size) => {
      context.font = LEGEND_FONT;
      const layout = toRadialLayout({
        viewport,
        plot,
        legendEntries,
        axisCaptions: resolvedCaptions,
        format,
        measure: text => context.measureText(text).width,
      });
      projected.current = projectRadialPoints(plot, layout);
      renderRadial({
        context,
        plot,
        layout,
        markType,
        hovered,
        format,
        surfaceColor,
        inkColor,
      });
    },
    [
      plot,
      legendEntries,
      resolvedCaptions,
      format,
      markType,
      hovered,
      surfaceColor,
      inkColor,
    ]
  );

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { containerRef, viewport } = useContainerSize();
  const { hasPainted } = useChartCanvas(paint, canvasRef, viewport);

  const onExportCanvasAvailableRef = useRef(onExportCanvasAvailable);
  onExportCanvasAvailableRef.current = onExportCanvasAvailable;
  const exportCaptionsRef = useRef(exportCaptions);
  exportCaptionsRef.current = exportCaptions;

  useEffect(() => {
    const publish = onExportCanvasAvailableRef.current;
    publish?.(() =>
      hasPainted()
        ? toExportCanvas({
            canvas: canvasRef.current,
            backgroundColor: surfaceColor,
            inkColor,
            captions: exportCaptionsRef.current ?? [],
          })
        : null
    );
    return () => publish?.(null);
  }, [surfaceColor, inkColor, hasPainted]);

  const onDrawableChangeRef = useRef(onDrawableChange);
  onDrawableChangeRef.current = onDrawableChange;
  const isDrawable = hasAnyValue(plot);

  useEffect(() => {
    onDrawableChangeRef.current?.(isDrawable);
  }, [isDrawable]);

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const cursor = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    const hit = findRadialPointAt(
      projected.current,
      cursor,
      hovered?.seriesIndex ?? null
    );
    setPointer(previous => toNextPointer({ previous, plot, at: cursor, hit }));
  };

  const onPointerLeave = () => setPointer(null);

  const onKeyDown = (event: React.KeyboardEvent<HTMLCanvasElement>) => {
    if (!isRadialNavigationKey(event.key)) return;
    event.preventDefault();
    const { key } = event;
    setPointer(previous =>
      toKeyboardPointer({
        plot,
        hover: toKeyboardHover(
          plot,
          toCurrentPointer(previous, plot)?.hover ?? null,
          key
        ),
        projected: projected.current,
      })
    );
  };

  const onBlur = () => setPointer(null);
  const isKeyboardHover = current?.source === 'keyboard';

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        background: surfaceColor,
        position: 'relative',
      }}
    >
      {!isDrawable ? (
        emptyState
      ) : (
        <>
          <canvas
            ref={canvasRef}
            role="img"
            aria-label={formatAccessibleName({
              seriesCount: plot.series.length,
              pointCount: countValues(plot),
            })}
            tabIndex={0}
            style={{ display: 'block', outlineOffset: -2 }}
            onPointerMove={onPointerMove}
            onPointerLeave={onPointerLeave}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
          />
          <div role="status" aria-live="polite" style={VISUALLY_HIDDEN}>
            {isKeyboardHover && hovered && (
              <RadialTooltip plot={plot} hovered={hovered} format={format} />
            )}
          </div>
          {hovered && tooltipAt && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: tooltipAt.x + 14,
                top: tooltipAt.y + 14,
                maxWidth: 'min(320px, 60%)',
                transform: tooltipFlipTransform({ at: tooltipAt, viewport }),
                padding: '6px 10px',
                borderRadius: 4,
                background: surfaceColor,
                color: inkColor,
                border: `1px solid ${tooltipBorderColor}`,
                font: '12px system-ui, -apple-system, "Segoe UI", sans-serif',
                pointerEvents: 'none',
              }}
            >
              <RadialTooltip plot={plot} hovered={hovered} format={format} />
            </div>
          )}
        </>
      )}
    </div>
  );
};
